/**
 * CNTLDrawer component
 *
 * Renders the Status / Attributes control row inline into a host container
 * (UnifiedDrawer's controls slot). The host owns the panel chrome, open/close,
 * and the visible handle; this component manages just the checkbox state +
 * change events and notifies the host of state for handle paint.
 */
import { injectStyles } from './styles.js';
import { ATTRIBUTE_REGISTRY } from '../attributes/registry.js';
// Shown in the Status/Attributes help pop-up (the "?" button beside each
// heading) - a plain array so adding more hints later is a one-line change.
const CNTL_HELP_HINTS = [
    'If you want to just see Open Houses, turn off For Sale and the other Status boxes, and enable just Open Houses.',
    'Want to see more Listings? Drag the Listing drawer open using the three green lines next to the Listing handle.',
];
export class CNTLDrawer {
    container;
    statuses;
    attributes;
    hiddenAttributes;
    onStatusChange;
    onAttributeChange;
    onHandleStateChange;
    constructor(options) {
        injectStyles();
        this.container = options.container;
        this.onStatusChange = options.onStatusChange;
        this.onAttributeChange = options.onAttributeChange;
        this.onHandleStateChange = options.onHandleStateChange;
        this.hiddenAttributes = new Set(options.initiallyHiddenAttributes ?? []);
        this.statuses = {
            forSale: options.initialStatuses?.forSale ?? true,
            pending: options.initialStatuses?.pending ?? false,
            recentlySold: options.initialStatuses?.recentlySold ?? false,
            offMarket: options.initialStatuses?.offMarket ?? false,
        };
        this.attributes = Object.fromEntries(ATTRIBUTE_REGISTRY.map((entry) => [entry.id, options.initialAttributes?.[entry.id] ?? false]));
        this.render();
        this.attachEventListeners();
        // Push initial handle state to the host so it can paint dots/icons.
        requestAnimationFrame(() => {
            this.emitHandleState();
        });
    }
    render() {
        this.container.innerHTML = `<div class="cntl-embedded">${this.renderSections()}</div>`;
    }
    renderSections() {
        return `
      <div class="cntl-section">
        <div class="cntl-section-label-row">
          <span class="cntl-section-label">Status</span>
          ${this.renderHelpButton()}
        </div>
        <label class="cntl-option">
          <input type="checkbox" id="cntl-for-sale" ${this.statuses.forSale ? 'checked' : ''}>
          <span class="cntl-option-label">
            <span class="cntl-option-dot for-sale"></span>
            For Sale
          </span>
        </label>
        <label class="cntl-option">
          <input type="checkbox" id="cntl-pending" ${this.statuses.pending ? 'checked' : ''}>
          <span class="cntl-option-label">
            <span class="cntl-option-dot pending"></span>
            Pending
          </span>
        </label>
        <label class="cntl-option">
          <input type="checkbox" id="cntl-recently-sold" ${this.statuses.recentlySold ? 'checked' : ''}>
          <span class="cntl-option-label">
            <span class="cntl-option-dot recently-sold"></span>
            Recently Sold
          </span>
        </label>
        <label class="cntl-option">
          <input type="checkbox" id="cntl-off-market" ${this.statuses.offMarket ? 'checked' : ''}>
          <span class="cntl-option-label">
            <span class="cntl-option-dot off-market"></span>
            Off Market
          </span>
        </label>
      </div>
      <div class="cntl-section">
        <div class="cntl-section-label-row">
          <span class="cntl-section-label">Attributes</span>
          ${this.renderHelpButton()}
        </div>
        <div class="cntl-attributes-grid">
          ${ATTRIBUTE_REGISTRY.filter((entry) => !this.hiddenAttributes.has(entry.id)).map((entry) => this.renderAttributeOption(entry)).join('')}
        </div>
      </div>
      <div class="cntl-help-popup" id="cntl-help-popup" hidden>
        <button type="button" class="cntl-help-popup-close" id="cntl-help-popup-close" aria-label="Close">&times;</button>
        <ul class="cntl-help-popup-list">
          ${CNTL_HELP_HINTS.map((hint) => `<li>${hint}</li>`).join('')}
        </ul>
      </div>
    `;
    }
    renderHelpButton() {
        return `<button type="button" class="cntl-help-btn" data-help-trigger aria-label="Tips" title="Tips">?</button>`;
    }
    renderAttributeOption(entry) {
        return `
      <label class="cntl-option">
        <input type="checkbox" id="cntl-${entry.id}" ${this.attributes[entry.id] ? 'checked' : ''}>
        <span class="cntl-option-label">
          <svg class="cntl-option-icon" style="color: ${entry.color}" viewBox="0 0 24 24" fill="currentColor">${entry.icon}</svg>
          ${entry.label}
        </span>
      </label>
    `;
    }
    emitHandleState() {
        this.onHandleStateChange?.({
            statuses: { ...this.statuses },
            attributes: { ...this.attributes },
        });
    }
    attachEventListeners() {
        const forSale = this.container.querySelector('#cntl-for-sale');
        const pending = this.container.querySelector('#cntl-pending');
        const recentlySold = this.container.querySelector('#cntl-recently-sold');
        const offMarket = this.container.querySelector('#cntl-off-market');
        forSale?.addEventListener('change', () => {
            this.statuses.forSale = forSale.checked;
            this.emitStatusChange();
        });
        pending?.addEventListener('change', () => {
            this.statuses.pending = pending.checked;
            this.emitStatusChange();
        });
        recentlySold?.addEventListener('change', () => {
            this.statuses.recentlySold = recentlySold.checked;
            this.emitStatusChange();
        });
        offMarket?.addEventListener('change', () => {
            this.statuses.offMarket = offMarket.checked;
            this.emitStatusChange();
        });
        this.attachAttributeListeners();
        this.attachHelpPopupListeners();
    }
    attachAttributeListeners() {
        ATTRIBUTE_REGISTRY.forEach((entry) => {
            const checkbox = this.container.querySelector(`#cntl-${entry.id}`);
            checkbox?.addEventListener('change', () => {
                this.attributes[entry.id] = checkbox.checked;
                this.emitAttributeChange();
            });
        });
    }
    // One shared pop-up, opened by either the Status or Attributes "?"
    // button - the hints aren't specific to one column, so there's no need
    // for two separate pop-ups.
    attachHelpPopupListeners() {
        const popup = this.container.querySelector('#cntl-help-popup');
        const triggers = Array.from(this.container.querySelectorAll('[data-help-trigger]'));
        const closeBtn = this.container.querySelector('#cntl-help-popup-close');
        if (!popup)
            return;
        triggers.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                popup.toggleAttribute('hidden');
            });
        });
        closeBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            popup.setAttribute('hidden', '');
        });
        // Click anywhere outside the popup (and outside the triggers, which
        // already toggle it themselves) closes it.
        document.addEventListener('click', (e) => {
            if (popup.hasAttribute('hidden'))
                return;
            const target = e.target;
            if (popup.contains(target) || triggers.some((btn) => btn.contains(target)))
                return;
            popup.setAttribute('hidden', '');
        });
    }
    emitStatusChange() {
        this.emitHandleState();
        this.onStatusChange?.({ ...this.statuses });
    }
    emitAttributeChange() {
        this.emitHandleState();
        this.onAttributeChange?.({ ...this.attributes });
    }
    // -------- Public API used by main.ts --------
    getStatuses() {
        return { ...this.statuses };
    }
    getAttributes() {
        return { ...this.attributes };
    }
    /**
     * Turn off every Status box and every Attribute except `attributeId`
     * (e.g. a `?story=` link isolating the map/drawer to Agent Picks on
     * load) - same end state as a user manually unchecking everything else
     * and checking just the one box (already a documented pattern, see the
     * Open Houses hint in CNTL_HELP_HINTS), just done in one call. The user
     * remains free to re-check anything afterward.
     */
    showOnlyAttribute(attributeId) {
        const statusCheckboxIds = {
            forSale: 'cntl-for-sale',
            pending: 'cntl-pending',
            recentlySold: 'cntl-recently-sold',
            offMarket: 'cntl-off-market',
        };
        Object.keys(this.statuses).forEach((key) => {
            this.statuses[key] = false;
            const checkbox = this.container.querySelector(`#${statusCheckboxIds[key]}`);
            if (checkbox)
                checkbox.checked = false;
        });
        ATTRIBUTE_REGISTRY.forEach((entry) => {
            const value = entry.id === attributeId;
            this.attributes[entry.id] = value;
            const checkbox = this.container.querySelector(`#cntl-${entry.id}`);
            if (checkbox)
                checkbox.checked = value;
        });
        this.emitStatusChange();
        this.emitAttributeChange();
    }
    /**
     * Shows/hides one attribute's checkbox in the grid entirely (e.g. Agent
     * Picks, which is only meaningful while a `?story=` pick is active).
     * Patches just the attributes grid's markup rather than a full render() -
     * a full render() would re-run attachEventListeners(), which re-attaches
     * attachHelpPopupListeners()'s document-level click listener without
     * removing the old one (a leak, since this can now fire more than once
     * over the drawer's lifetime, unlike the original construction-only
     * call). Hiding also turns the attribute off, so a stale "checked" state
     * doesn't linger for if it becomes visible again later.
     */
    setAttributeVisible(id, visible) {
        const isHidden = this.hiddenAttributes.has(id);
        if (visible === !isHidden)
            return;
        if (visible) {
            this.hiddenAttributes.delete(id);
        }
        else {
            this.hiddenAttributes.add(id);
            this.attributes[id] = false;
        }
        const grid = this.container.querySelector('.cntl-attributes-grid');
        if (grid) {
            grid.innerHTML = ATTRIBUTE_REGISTRY
                .filter((entry) => !this.hiddenAttributes.has(entry.id))
                .map((entry) => this.renderAttributeOption(entry))
                .join('');
            this.attachAttributeListeners();
        }
        this.emitAttributeChange();
    }
    /**
     * Checks/unchecks one attribute WITHOUT touching Status or any other
     * Attribute box - unlike showOnlyAttribute() (Agent Picks' exclusive
     * isolation), this is for attributes meant to sit alongside whatever's
     * already selected, e.g. auto-enabling Featured on a `?agent=` link.
     * No-ops silently if the attribute is currently hidden (setAttributeVisible)
     * - nothing to check if the box doesn't exist.
     */
    setAttributeChecked(id, checked) {
        if (this.hiddenAttributes.has(id))
            return;
        this.attributes[id] = checked;
        const checkbox = this.container.querySelector(`#cntl-${id}`);
        if (checkbox)
            checkbox.checked = checked;
        this.emitAttributeChange();
    }
    /** Get active status values in MLS-API form (Active / Pending / Sold / OffMarket). */
    getActiveStatuses() {
        const statuses = [];
        if (this.statuses.forSale)
            statuses.push('Active');
        if (this.statuses.pending)
            statuses.push('Pending');
        if (this.statuses.recentlySold)
            statuses.push('Sold');
        if (this.statuses.offMarket)
            statuses.push('OffMarket');
        return statuses;
    }
}
//# sourceMappingURL=CNTLDrawer.js.map